/**
 * 视频图层插入画布时的显示尺寸常量与计算
 *
 * 业务规则：画布显示宽度按生成质量档位固定，高度按封面/视频原始比例自适应。
 * 不使用封面图像素尺寸直接作为 Fabric 显示尺寸，避免同档视频忽大忽小。
 */

import { fabric } from "fabric";

export type VideoCanvasQuality = "480p" | "720p" | "1080p";

/** 缺省按 720p 展示 */
export const DEFAULT_VIDEO_CANVAS_QUALITY: VideoCanvasQuality = "720p";

/**
 * 中心控制按钮（播放 / 暂停）尺寸策略：
 * - 相对视频在「屏幕上」的短边取固定比例，保证随画布缩放与图层大小成比例
 * - 再用屏幕像素上下限夹紧：缩放很小时不至于点不动，很大时不至于挡住画面
 *
 * 播放（FabricVideo）与暂停（CanvasVideoPlayer）必须共用同一套计算。
 */
/** 相对视频屏幕短边的比例 */
export const VIDEO_CENTER_CONTROL_RATIO = 0.12;
/** 屏幕像素下限：画布缩得很小时仍可点击 */
export const VIDEO_CENTER_CONTROL_MIN_PX = 24;
/** 屏幕像素上限：视频很大/放大时不遮挡过多内容 */
export const VIDEO_CENTER_CONTROL_MAX_PX = 64;
/** 暂停态内图标相对按钮边长的比例（原 24/48） */
export const VIDEO_CENTER_CONTROL_ICON_RATIO = 0.5;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/**
 * 按视频屏幕短边比例计算中心控制按钮的屏幕边长（px）
 */
export function resolveVideoCenterControlScreenSize(
  screenWidth: number,
  screenHeight: number,
): number {
  const shortSide = Math.min(Math.max(screenWidth, 0), Math.max(screenHeight, 0));
  return clamp(
    shortSide * VIDEO_CENTER_CONTROL_RATIO,
    VIDEO_CENTER_CONTROL_MIN_PX,
    VIDEO_CENTER_CONTROL_MAX_PX,
  );
}

/**
 * 计算视频对象本地 1 单位对应的屏幕像素（含视口与父组缩放）
 * 用于把屏幕尺寸换算到 Fabric 本地坐标
 */
export function resolveVideoObjectDisplayScale(obj: fabric.Object): number {
  const objectMatrix = obj.calcTransformMatrix();
  const canvas = obj.canvas;
  if (!canvas?.viewportTransform) {
    const { scaleX, scaleY } = fabric.util.qrDecompose(objectMatrix);
    return Math.max(Math.abs(scaleX), Math.abs(scaleY), 1e-6);
  }

  const screenMatrix = fabric.util.multiplyTransformMatrices(canvas.viewportTransform, objectMatrix);
  const { scaleX, scaleY } = fabric.util.qrDecompose(screenMatrix);
  return Math.max(Math.abs(scaleX), Math.abs(scaleY), 1e-6);
}

/**
 * 视频对象在屏幕上的宽高（已含视口缩放）
 */
export function resolveVideoObjectScreenSize(obj: fabric.Object): { width: number; height: number } {
  const w = Math.max(obj.width || 0, 0);
  const h = Math.max(obj.height || 0, 0);
  const objectMatrix = obj.calcTransformMatrix();
  const canvas = obj.canvas;
  const screenMatrix = canvas?.viewportTransform
    ? fabric.util.multiplyTransformMatrices(canvas.viewportTransform, objectMatrix)
    : objectMatrix;
  const { scaleX, scaleY } = fabric.util.qrDecompose(screenMatrix);
  return {
    width: Math.abs(w * scaleX),
    height: Math.abs(h * scaleY),
  };
}

/**
 * 中心播放/暂停按钮在对象本地坐标系下的边长
 * 屏幕观感与 resolveVideoCenterControlScreenSize 一致
 */
export function resolveVideoCenterControlLocalSize(obj: fabric.Object): number {
  const { width, height } = resolveVideoObjectScreenSize(obj);
  const screenSize = resolveVideoCenterControlScreenSize(width, height);
  return screenSize / resolveVideoObjectDisplayScale(obj);
}

/** 质量档位 → 画布显示宽度（px） */
export const VIDEO_CANVAS_DISPLAY_WIDTH_BY_QUALITY: Record<VideoCanvasQuality, number> = {
  "480p": 1536,
  "720p": 2048,
  "1080p": 2560,
};

/**
 * 将任意 quality / videoResolution 归一为画布展示用档位
 * 兼容 "720p"、720、"720"、旧格式 1K/2K 等
 */
export function normalizeVideoCanvasQuality(
  quality?: string | number | null,
): VideoCanvasQuality {
  const raw = String(quality ?? "").trim().toLowerCase();
  if (!raw) return DEFAULT_VIDEO_CANVAS_QUALITY;
  if (raw.includes("1080") || raw === "2k") return "1080p";
  if (raw.includes("480")) return "480p";
  if (raw.includes("720") || raw === "1k") return "720p";
  return DEFAULT_VIDEO_CANVAS_QUALITY;
}

/** 解析质量档位对应的画布显示宽度 */
export function resolveVideoCanvasDisplayWidth(
  quality?: string | number | null,
): number {
  return VIDEO_CANVAS_DISPLAY_WIDTH_BY_QUALITY[normalizeVideoCanvasQuality(quality)];
}

/**
 * 根据质量档位与原始宽高计算画布显示尺寸（宽固定、高等比）
 */
export function resolveVideoCanvasDisplaySize(
  quality: string | number | null | undefined,
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number } {
  const width = resolveVideoCanvasDisplayWidth(quality);
  if (!(naturalWidth > 0) || !(naturalHeight > 0)) {
    // 无有效比例时回退 16:9，贴近视频常见画幅
    return { width, height: Math.round((width * 9) / 16) };
  }

  const height = Math.round(naturalHeight * (width / naturalWidth));
  return { width, height };
}

/**
 * 将目标显示尺寸映射为 Fabric 基线宽高 + 缩放比
 * （封面图保持原始像素作为 width/height，用 scale 控制舞台显示大小）
 */
export function resolveVideoFabricScale(
  naturalWidth: number,
  naturalHeight: number,
  targetWidth?: number,
  targetHeight?: number,
  quality?: string | number | null,
): {
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  displayWidth: number;
  displayHeight: number;
} {
  const fallbackWidth = resolveVideoCanvasDisplayWidth(quality);
  const naturalW = naturalWidth > 0 ? naturalWidth : fallbackWidth;
  const naturalH = naturalHeight > 0 ? naturalHeight : Math.round((fallbackWidth * 9) / 16);
  const fallback = resolveVideoCanvasDisplaySize(quality, naturalW, naturalH);
  const displayWidth = targetWidth ?? fallback.width;
  const displayHeight =
    targetHeight ?? Math.round(naturalH * (displayWidth / naturalW));

  return {
    width: naturalW,
    height: naturalH,
    scaleX: displayWidth / naturalW,
    scaleY: displayHeight / naturalH,
    displayWidth,
    displayHeight,
  };
}
